import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SpaceBetween, Header, Container, ExpandableSection, BreadcrumbGroup, Box, StatusIndicator } from '@cloudscape-design/components';
import Markdown from 'markdown-to-jsx';
import { Layout } from '../components/layout';
import { FAQ } from '../types';
import { getEndpoint } from '../utils';
import { useAuth } from 'react-oidc-context';
import { RecordPageView } from '../utils/rum';

export default function FAQPage() {
  RecordPageView();
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [sections, setSections] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const auth = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (auth.isAuthenticated) {
        try {
          // Fetch FAQs
          const faqResponse = await fetch(getEndpoint('/api/faq'), {
            headers: {
              Authorization: `Bearer ${auth.user?.id_token}`,
            },
          });
          const faqData = await faqResponse.json();
          setFaqs(faqData);

          // Fetch sections
          const sectionsResponse = await fetch(getEndpoint('/api/sections'), {
            headers: {
              Authorization: `Bearer ${auth.user?.id_token}`,
            },
          });
          const sectionsData = await sectionsResponse.json();
          
          // Only include sections that have FAQs and sort by order
          const sectionsWithFaqs = sectionsData
            .filter((section: any) => faqData.some((faq: FAQ) => faq.section === section.PK.replace('FAQ#', '')))
            .sort((a: any, b: any) => (a.order || 999) - (b.order || 999))
            .map((section: any) => section.PK.replace('FAQ#', ''));
          
          setSections(sectionsWithFaqs);
        } catch (error) {
          console.error('Error fetching FAQs:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [auth]);

  return (
    <Layout breadcrumbs={
      <BreadcrumbGroup
        onClick={({ detail }) => navigate(detail.href)}
        items={[
          { text: "Home", href: "/"},
          { text: "FAQs", href: "/faq" }
        ]}
      />
    }>
      <SpaceBetween direction="vertical" size="l">
        <Header variant="h1">Frequently Asked Questions</Header>
        {loading ? (
          <Container>
            <StatusIndicator type="loading">Loading FAQs...</StatusIndicator>
          </Container>
        ) : (
          sections.map((section) => (
            <Container key={section}>
              <SpaceBetween direction="vertical" size="l">
                <Header variant="h2">{section}</Header>
                {faqs
                  .filter((faq) => faq.section === section)
                  .map((faq) => (
                    <ExpandableSection key={faq.id} headerText={faq.question}>
                      <Box color="text-body-secondary">
                        <Markdown>{faq.answer}</Markdown>
                      </Box>
                    </ExpandableSection>
                  ))}
              </SpaceBetween>
            </Container>
          ))
        )}
      </SpaceBetween>
    </Layout>
  );
}
